<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import FunnelAPI from 'dashboard/api/funnel';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: () => ({ funnel_id: null, funnel_stage: null }),
  },
  layout: {
    type: String,
    default: 'stacked',
    validator: v => ['stacked', 'inline'].includes(v),
  },
});

const emit = defineEmits(['update:modelValue']);

const funnels = ref([]);
const selectedFunnelId = ref(null);
const selectedStageId = ref(null);
const loading = ref(false);

const selectedFunnelFull = computed(() => {
  if (!selectedFunnelId.value) return null;
  return (
    funnels.value.find(f => String(f.id) === String(selectedFunnelId.value)) ||
    null
  );
});

const availableStages = computed(() => {
  const f = selectedFunnelFull.value;
  if (!f?.stages) return [];
  return Object.entries(f.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name || id,
      position: stage.position || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

const funnelOptions = computed(() =>
  funnels.value.map(f => ({ id: f.id, name: f.name }))
);

const stageOptions = computed(() =>
  availableStages.value.map(({ id, name }) => ({ id, name }))
);

const funnelSelectModel = computed({
  get() {
    if (!selectedFunnelId.value) return null;
    const f = selectedFunnelFull.value;
    return f ? { id: f.id, name: f.name } : null;
  },
  set(val) {
    selectedFunnelId.value = val?.id != null ? String(val.id) : null;
    selectedStageId.value = null;
    updateValue();
  },
});

const stageSelectModel = computed({
  get() {
    if (!selectedStageId.value || !selectedFunnelId.value) return null;
    const s = availableStages.value.find(
      st => String(st.id) === String(selectedStageId.value)
    );
    return s ? { id: s.id, name: s.name } : null;
  },
  set(val) {
    selectedStageId.value = val?.id ?? null;
    updateValue();
  },
});

const updateValue = () => {
  const value = {
    funnel_id:
      selectedFunnelId.value != null && selectedFunnelId.value !== ''
        ? parseInt(selectedFunnelId.value, 10)
        : null,
    funnel_stage: selectedStageId.value || null,
  };
  emit('update:modelValue', value);
};

const fetchFunnels = async () => {
  loading.value = true;
  try {
    const response = await FunnelAPI.get();
    funnels.value = (response.data || []).map(funnel => ({
      ...funnel,
      id: String(funnel.id),
    }));
  } catch (error) {
    console.error('Erro ao buscar funis:', error);
  } finally {
    loading.value = false;
  }
};

const syncFromModelValue = async () => {
  const modelData = Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue;

  if (!modelData?.funnel_id) {
    selectedFunnelId.value = null;
    selectedStageId.value = null;
    return;
  }

  if (funnels.value.length === 0) return;

  const funnel = funnels.value.find(
    f => String(f.id) === String(modelData.funnel_id)
  );
  if (!funnel) return;

  selectedFunnelId.value = String(funnel.id);
  await nextTick();

  if (modelData.funnel_stage) {
    const stageId = modelData.funnel_stage;
    await nextTick();

    let stage = availableStages.value.find(s => String(s.id) === String(stageId));

    if (stage) {
      selectedStageId.value = stage.id;
    } else if (funnel.stages?.[stageId]) {
      selectedStageId.value = stageId;
    } else {
      setTimeout(() => {
        const stageRetry = availableStages.value.find(
          s => String(s.id) === String(stageId)
        );
        if (stageRetry) {
          selectedStageId.value = stageRetry.id;
        }
      }, 200);
    }
  }
};

watch(() => props.modelValue, syncFromModelValue, { deep: true, immediate: true });

watch(funnels, () => {
  if (funnels.value.length > 0) {
    syncFromModelValue();
  }
});

watch(availableStages, newStages => {
  if (newStages.length === 0) return;

  const modelData = Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue;

  if (modelData?.funnel_stage && !selectedStageId.value) {
    const stageId = modelData.funnel_stage;
    const stage = newStages.find(s => String(s.id) === String(stageId));
    if (stage) {
      selectedStageId.value = stage.id;
    }
  }
});

const rootClass = computed(() =>
  props.layout === 'inline'
    ? 'flex flex-row flex-wrap items-center gap-2 flex-1 min-w-0'
    : 'flex flex-col gap-2 mt-1'
);

const fieldClass = computed(() =>
  props.layout === 'inline' ? 'flex-1 min-w-[10rem]' : 'w-full'
);

onMounted(async () => {
  await fetchFunnels();
  await syncFromModelValue();
  updateValue();
});
</script>

<template>
  <div
    class="multiselect-wrap--small"
    :class="[rootClass, loading && 'opacity-60 pointer-events-none']"
  >
    <SingleSelect
      v-model="funnelSelectModel"
      :options="funnelOptions"
      :placeholder="$t('AUTOMATION.ACTION.FUNNEL_SELECT_PLACEHOLDER')"
      disable-deselect
      :class="fieldClass"
      dropdown-max-height="max-h-60"
    />
    <SingleSelect
      v-model="stageSelectModel"
      :options="stageOptions"
      :placeholder="$t('AUTOMATION.ACTION.STAGE_SELECT_PLACEHOLDER')"
      disable-deselect
      :class="fieldClass"
      dropdown-max-height="max-h-60"
    />
  </div>
</template>
